import React, { useState } from 'react';
import { MapPin, Mail, Clock, CheckCircle, Loader2 } from 'lucide-react';
import FileInput from '../components/FileInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ nom: '', prenom: '', email: '', telephone: '', message: '' });
  const [fichier, setFichier] = useState(null);
  const [status, setStatus] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (fichier) fd.append('fichier', fichier);
      const res = await fetch(`${API}/contact`, { method: 'POST', body: fd });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Erreur serveur');
      }
      setStatus('success');
      setForm({ nom: '', prenom: '', email: '', telephone: '', message: '' });
      setFichier(null);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Une erreur est survenue. Veuillez réessayer.');
    }
  };

  return (
    <div className="bg-[#FAFAFA] min-h-screen pt-16" data-testid="contact-page">
      {/* Header */}
      <div className="py-16 px-4 bg-[#F5F5F5] border-b border-gray-100">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] mb-3" style={{ color: '#80a890' }}>
            Nous contacter
          </p>
          <h1
            className="text-5xl sm:text-6xl font-black mb-4"
            style={{ fontFamily: 'Barlow, sans-serif', color: '#2A3630' }}
          >
            Bonjour !
          </h1>
          <p className="text-gray-500 text-base max-w-xl mx-auto">
            Une question, une suggestion ou simplement envie de nous faire un retour ?
            Écrivez-nous, on vous répond rapidement.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-16 grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Contact info */}
        <div data-testid="contact-info">
          <h2
            className="text-3xl font-black mb-8"
            style={{ fontFamily: 'Barlow, sans-serif', color: '#2A3630' }}
          >
            Informations
          </h2>

          <div className="space-y-5">
            <div className="flex items-start gap-4 p-5 bg-[#F5F5F5] rounded-2xl border border-gray-100">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{ backgroundColor: 'rgba(128,168,144,0.15)' }}
              >
                <Mail size={16} style={{ color: '#80a890' }} />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">Email</p>
                <a href="mailto:contact@grainette.fr" className="text-sm text-gray-500 hover:text-sage transition-colors mt-0.5 block" data-testid="contact-email">
                  contact@grainette.fr
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4 p-5 bg-[#F5F5F5] rounded-2xl border border-gray-100">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{ backgroundColor: 'rgba(128,168,144,0.15)' }}
              >
                <Clock size={16} style={{ color: '#80a890' }} />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">Horaires</p>
                <p className="text-sm text-gray-500 mt-0.5">Lundi – Vendredi</p>
                <p className="text-sm text-gray-500">Salades · 11h30 – 15h00</p>
                <p className="text-sm text-gray-500">Pâtes · 11h30 – 14h30</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-5 bg-[#F5F5F5] rounded-2xl border border-gray-100">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{ backgroundColor: 'rgba(128,168,144,0.15)' }}
              >
                <MapPin size={16} style={{ color: '#80a890' }} />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">Nos adresses</p>
                <p className="text-sm text-gray-500 mt-0.5">27 rue de Mogador, 75009 Paris</p>
                <p className="text-sm text-gray-500">16 & 17 rue Godot de Mauroy, 75009 Paris</p>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div data-testid="contact-form-container">
          <h2
            className="text-3xl font-black mb-6"
            style={{ fontFamily: 'Barlow, sans-serif', color: '#2A3630' }}
          >
            Envoyez-nous un message
          </h2>

          {status === 'success' ? (
            <div
              className="flex flex-col items-center justify-center p-10 bg-[#F5F5F5] rounded-2xl border border-gray-100 text-center"
              data-testid="contact-success"
            >
              <CheckCircle size={48} style={{ color: '#80a890' }} className="mb-4" />
              <h3 className="text-xl font-black text-gray-800 mb-2" style={{ fontFamily: 'Barlow, sans-serif' }}>Message envoyé !</h3>
              <p className="text-gray-500 text-sm">
                Merci pour votre message. Nous vous répondrons dans les plus brefs délais.
              </p>
              <button
                onClick={() => setStatus(null)}
                className="mt-6 px-6 py-2.5 rounded-full text-sm font-semibold text-white btn-lift"
                style={{ backgroundColor: '#80a890' }}
                data-testid="send-another-contact-btn"
              >
                Envoyer un autre message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-[#F5F5F5] rounded-2xl border border-gray-100 p-6 space-y-4" data-testid="contact-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="contact-prenom">
                    Prénom *
                  </label>
                  <input
                    id="contact-prenom"
                    name="prenom"
                    type="text"
                    required
                    value={form.prenom}
                    onChange={handleChange}
                    data-testid="contact-input-prenom"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-sage transition-colors"
                    placeholder="Marie"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="contact-nom">
                    Nom *
                  </label>
                  <input
                    id="contact-nom"
                    name="nom"
                    type="text"
                    required
                    value={form.nom}
                    onChange={handleChange}
                    data-testid="contact-input-nom"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-sage transition-colors"
                    placeholder="Martin"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="contact-email">
                  Email *
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  data-testid="contact-input-email"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-sage transition-colors"
                  placeholder="marie.martin@email.fr"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="contact-telephone">
                  Téléphone
                </label>
                <input
                  id="contact-telephone"
                  name="telephone"
                  type="tel"
                  value={form.telephone}
                  onChange={handleChange}
                  data-testid="contact-input-telephone"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-sage transition-colors"
                  placeholder="06 XX XX XX XX"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="contact-message">
                  Message *
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={5}
                  required
                  value={form.message}
                  onChange={handleChange}
                  data-testid="contact-input-message"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-sage transition-colors resize-none"
                  placeholder="Votre message..."
                />
              </div>

              <FileInput
                file={fichier}
                onChange={setFichier}
                label="Pièce jointe (optionnel)"
              />

              {status === 'error' && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600" data-testid="contact-error">
                  {errorMsg}
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'loading'}
                data-testid="submit-contact-btn"
                className="w-full py-3.5 font-semibold text-white rounded-xl text-sm btn-lift flex items-center justify-center gap-2 disabled:opacity-70"
                style={{ backgroundColor: '#80a890' }}
              >
                {status === 'loading' ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Envoi...
                  </>
                ) : (
                  'Envoyer mon message'
                )}
              </button>

              <p className="text-xs text-gray-400 text-center">
                En soumettant ce formulaire, j'accepte que les informations saisies soient utilisées pour traiter ma demande.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
